import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type { FramesManager } from 'shared-libs/components/view/frames-manager'
import type { UI2SwitchFieldProps } from 'shared-libs/components/ui2/fields/ui2-switch-field/ui2-switch-field-props'
import { UI2_SWITCH_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-switch-field/ui2-switch-field-props'
import {
  type PrefilledFieldState,
  beginPrefilledEdit,
  cancelPrefilledEdit,
  createPrefilledFieldState,
  hasFieldValue,
} from 'shared-libs/components/ui2/fields/core-field/bound-field-state'

import { UI2PlatformContext } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'
import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { IUI2FieldShellProps, UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { UI2Label } from '../field-ui/label/label'
import {
  UI2PrefilledCancelButton,
  UI2PrefilledEditButton,
} from '../field-ui/prefilled-button/prefilled-button'
import './_ui2-switch-field.scss'

interface IUI2SwitchWebExtras {
  onChange?: (value: any) => void
  className?: string
  style?: React.CSSProperties
  frames?: FramesManager
  translationTable?: any
  isHorizontalLayout?: boolean
  errors?: any[]
}

/**
 * @uiComponent
 */
export interface IUI2SwitchFieldProps extends UI2SwitchFieldProps, IUI2SwitchWebExtras {}

interface IUI2SwitchFieldState {
  // Used only in two-column mode; horizontal shell manages its own prefilled state.
  prefilled: PrefilledFieldState<boolean>
}

export class UI2SwitchField extends React.Component<IUI2SwitchFieldProps, IUI2SwitchFieldState> {
  public static defaultProps: Partial<IUI2SwitchFieldProps> = {
    ...UI2_SWITCH_FIELD_DEFAULT_PROPS,
  }

  constructor(props: IUI2SwitchFieldProps) {
    super(props)
    this.state = {
      prefilled: createPrefilledFieldState(props.value, props.enablePrefilledMode ?? false),
    }
  }

  public componentDidUpdate(prevProps: IUI2SwitchFieldProps): void {
    // Same test render() uses — the horizontal shell owns its own prefilled
    // state, so this reset is two-column-only and must agree with the branch.
    if (!this.usesTwoColumn) return
    const { prefilled } = this.state
    const isPrefilledDisplay = prefilled.originalValue !== undefined && !prefilled.isEditing
    if (!isPrefilledDisplay) return
    if (hasFieldValue(prevProps.value) && !hasFieldValue(this.props.value)) {
      this.setState(s => ({ prefilled: { ...s.prefilled, originalValue: undefined, isEditing: true } }))
    }
  }


  // MW always renders two-column, regardless of isHorizontalLayout.
  //
  // `withUI2MobileWeb` already force-sets the prop to false, but
  // `withUI2FormContext` — which wraps *inside* it — then replaces it with
  // this class's own `defaultProps.isHorizontalLayout` whenever the uiSchema
  // node doesn't set it explicitly. So the HOC's intent is silently undone
  // for any field defaulting to horizontal, and the platform has to be read
  // here. See the layout section in `.claude/rules/webapp.md`.
  public static contextType = UI2PlatformContext
  declare context: React.ContextType<typeof UI2PlatformContext>

  private get usesTwoColumn(): boolean {
    return this.context === 'mobileWeb' || this.props.isHorizontalLayout === false
  }

  public render() {
    return this.usesTwoColumn ? this.renderTwoColumn() : this.renderHorizontal()
  }

  private renderHorizontal() {
    const { className, value, isDisabled, accessibilityLabel, label, testID } = this.props
    const renderReadOnly = (val: boolean | undefined, { editNode }: { editNode?: React.ReactNode }) => (
      <>
        <div className='ui2-fieldShell-readOnly-content'>
          <span className='ui2-switchField-readOnlyText'>{val ? 'Yes' : 'No'}</span>
        </div>
        {editNode}
      </>
    )
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-switchField', className)}
        renderReadOnly={renderReadOnly}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
      >
        <UI2FieldControl>
          <label className='ui2-switchField-horizontalControl'>
            <input
              type='checkbox'
              className='ui2-switchField-input'
              checked={!!value}
              disabled={!!isDisabled}
              onChange={this.handleChange}
              data-testid={testID}
              aria-label={accessibilityLabel || label}
              role='switch'
            />
            {this.renderSlider(!!value, !!isDisabled)}
          </label>
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderTwoColumn() {
    const {
      label, tooltip: tooltipText, inlineHelpText, translationTable, frames,
      className, style, isDisabled, isReadOnly, value, accessibilityLabel, testID,
      enablePrefilledMode, errors, isRequired, isOptional,
    } = this.props
    const { prefilled } = this.state
    const isPrefilledDisplay = prefilled.originalValue !== undefined && !prefilled.isEditing
    const showAsReadOnly = !!isReadOnly || isPrefilledDisplay
    const errorText = this.getErrorText()
    const hasError = !_.isEmpty(errors)
    const showEditButton = isPrefilledDisplay && !!enablePrefilledMode && !isDisabled && !isReadOnly
    const showCancelButton =
      prefilled.isEditing && prefilled.originalValue !== undefined &&
      !!enablePrefilledMode && !isDisabled && !isReadOnly

    if (showAsReadOnly) {
      return (
        <div
          className={classNames('ui2-switchField', 'ui2-switchField--readOnlyStack', className, {
            'ui2-switchField--disabled': isDisabled,
            'ui2-switchField--readOnly': true,
          })}
          style={style}
        >
          <UI2Label
            labelText={label}
            tooltipText={tooltipText}
            isRequired={isRequired}
            isOptional={isOptional}
            inlineHelpText={inlineHelpText}
            translationTable={translationTable}
            frames={frames}
          />
          <div className='ui2-switchField-readOnlyValue'>
            <span className='ui2-switchField-readOnlyText'>{value ? 'Yes' : 'No'}</span>
            {showEditButton && (
              <UI2PrefilledEditButton onPress={this.handleEdit} />
            )}
          </div>
        </div>
      )
    }

    return (
      <div
        className={classNames('ui2-switchField', 'ui2-switchField--twoColumn', className, {
          'ui2-switchField--disabled': isDisabled,
          'ui2-switchField--error': hasError,
        })}
        style={style}
      >
        <div className='ui2-switchField-rowWrapper'>
          <label className='ui2-switchField-row'>
            <div className='ui2-switchField-switchColumn'>
              <input
                type='checkbox'
                className='ui2-switchField-input'
                checked={!!value}
                disabled={!!isDisabled}
                onChange={this.handleChange}
                data-testid={testID}
                aria-label={accessibilityLabel || label}
                role='switch'
              />
              {this.renderSlider(!!value, !!isDisabled)}
            </div>
            <div className='ui2-switchField-labelColumn'>
              <UI2Label
                labelText={label}
                tooltipText={tooltipText}
                isRequired={isRequired}
                isOptional={isOptional}
                inlineHelpText={inlineHelpText}
                translationTable={translationTable}
                frames={frames}
              />
            </div>
          </label>
          {showCancelButton && (
            <UI2PrefilledCancelButton
              className='ui2-switchField-cancelButton'
              onPress={this.handleCancel}
            />
          )}
        </div>
        {errorText && <div className='ui2-switchField-errorText'>{errorText}</div>}
      </div>
    )
  }

  private renderSlider(checked: boolean, isDisabled: boolean) {
    return (
      <span
        className={classNames('ui2-switchField-track', {
          'ui2-switchField-track--checked': checked,
          'ui2-switchField-track--disabled': isDisabled,
        })}
        aria-hidden='true'
      >
        <span className='ui2-switchField-thumb' />
      </span>
    )
  }

  private getErrorText(): string | undefined {
    const { errors } = this.props
    if (_.isEmpty(errors)) return undefined
    const first = errors![0]
    return _.isString(first) ? first : (first?.detail || first?.message)
  }

  private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange?.(event.target.checked)
  }

  private handleEdit = () => {
    this.setState(s => ({ prefilled: beginPrefilledEdit(s.prefilled) }))
  }

  private handleCancel = () => {
    this.props.onChange?.(this.state.prefilled.originalValue as any)
    this.setState(s => ({ prefilled: cancelPrefilledEdit(s.prefilled) }))
  }
}
